<template>
  <!-- 敏感词分类管理模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleCancel"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              敏感词分类管理
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              创建和管理敏感词分类，设置不同严重级别
            </p>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="flex space-x-4 sm:space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
                'flex-1 sm:flex-initial text-center sm:text-left',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 创建分类 Tab -->
        <div v-show="activeTab === 'create'" class="space-y-6 mb-6">
          <div>
            <label class="text-sm font-medium text-gray-700 mb-3 block">创建新分类</label>
            <div class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分类名称 <span class="text-red-500">*</span></label>
                  <input
                    v-model="newCategory.name"
                    type="text"
                    placeholder="输入分类名称"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                    @keyup.enter="handleCreateCategory"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">严重级别 <span class="text-red-500">*</span></label>
                  <select
                    v-model="newCategory.severity_level"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                  >
                    <option value="low">低风险</option>
                    <option value="medium">中风险</option>
                    <option value="high">高风险</option>
                    <option value="forbidden">禁止使用</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">分类描述</label>
                <textarea
                  v-model="newCategory.description"
                  placeholder="描述该分类用途（可选）"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  :disabled="submitting"
                />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div class="flex items-center">
                  <input
                    v-model="newCategory.is_active"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    :disabled="submitting"
                  />
                  <label class="ml-2 block text-sm text-gray-700">启用分类</label>
                </div>
                <div>
                  <button
                    @click="handleCreateCategory"
                    :disabled="!newCategory.name.trim() || submitting"
                    class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {{ submitting ? '创建中...' : '创建分类' }}
                  </button>
                </div>
              </div>

              <div v-if="Object.keys(errors).length > 0" class="mt-4">
                <p v-for="(error, key) in errors" :key="key" class="text-sm text-red-600">{{ error }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 分类管理 Tab -->
        <div v-show="activeTab === 'manage'" class="space-y-6">
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">
                敏感词分类 
                <span class="text-sm text-gray-500">({{ filteredCategories.length }}{{ searchQuery ? ' / ' + categories.length : '' }})</span>
              </label>
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索分类..."
                  class="w-32 sm:w-48 h-8 pl-8 pr-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <svg class="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div v-if="loading" class="h-[30vh] flex flex-col items-center justify-center">
              <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <div v-else-if="filteredCategories.length === 0" class="h-[30vh] flex flex-col items-center justify-center text-center text-gray-500">
              <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p class="text-lg font-medium mb-2">
                {{ searchQuery ? '未找到匹配的分类' : '暂无分类' }}
              </p>
              <p class="text-sm">
                {{ searchQuery ? '尝试使用其他关键词搜索' : '点击"创建分类"标签页创建第一个敏感词分类' }}
              </p>
            </div>

            <div v-else class="h-[30vh] overflow-y-auto pr-2 -mr-2" style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;">
              <div class="space-y-2">
                <div
                  v-for="category in filteredCategories"
                  :key="category.id"
                  class="flex items-center justify-between p-3 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                >
                  <!-- 分类显示 -->
                  <div class="flex items-center space-x-3 min-w-0 flex-1 overflow-hidden">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium text-gray-900 truncate">{{ category.name }}</span>
                        <span
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="getSeverityBadgeClass(category.severity_level)"
                        >
                          {{ getSeverityText(category.severity_level) }}
                        </span>
                        <span
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                        >
                          {{ category.is_active ? '启用' : '禁用' }}
                        </span>
                      </div>
                      <div class="flex items-center text-xs text-gray-500 mt-1">
                        <span>{{ category.word_count || 0 }} 个敏感词</span>
                        <span v-if="category.description" class="ml-2 truncate">{{ category.description }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 操作按钮 -->
                  <div class="flex items-center space-x-1 ml-2 flex-shrink-0">
                    <button
                      @click="handleEditCategory(category)"
                      class="text-blue-600 hover:text-blue-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center"
                      title="编辑分类"
                    >
                      ✏️
                    </button>
                    <button
                      @click="handleDeleteCategory(category)"
                      :disabled="category.word_count > 0"
                      class="text-red-600 hover:text-red-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center disabled:text-gray-400 disabled:cursor-not-allowed"
                      :title="category.word_count > 0 ? '该分类下还有敏感词，无法删除' : '删除分类'"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="flex justify-end pt-6 border-t border-gray-200">
          <button
            @click="handleCancel"
            type="button"
            class="inline-flex items-center px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 编辑分类模态框 -->
  <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50">
    <div class="bg-white rounded-lg shadow-xl w-[90vw] max-w-[90vw] sm:max-w-lg mx-auto">
      <div class="p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">编辑敏感词分类</h3>
          <button
            @click="handleCloseEditModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>
        
        <!-- 编辑表单 -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分类名称</label>
            <input
              v-model="editingCategory.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="输入分类名称"
              :disabled="submitting"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">严重级别</label>
            <select
              v-model="editingCategory.severity_level"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              :disabled="submitting"
            >
              <option value="low">低风险</option>
              <option value="medium">中风险</option>
              <option value="high">高风险</option>
              <option value="forbidden">禁止使用</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分类描述</label>
            <textarea
              v-model="editingCategory.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="输入分类描述（可选）"
              :disabled="submitting"
            />
          </div>
          <div class="flex items-center">
            <input
              v-model="editingCategory.is_active"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              :disabled="submitting"
            />
            <label class="ml-2 block text-sm text-gray-700">启用分类</label>
          </div>
        </div>
        
        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3 mt-6">
          <button
            @click="handleCloseEditModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            @click="handleUpdateCategory"
            :disabled="!editingCategory.name.trim() || submitting"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ submitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { sensitiveWordCategoryService } from '@/services/sensitiveWordService'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()

// 状态管理
const loading = ref(false)
const submitting = ref(false)
const categories = ref([])
const searchQuery = ref('')
const errors = ref({})

// Tab 管理
const activeTab = ref('create')
const tabs = ref([
  { id: 'create', name: '创建分类' },
  { id: 'manage', name: '分类管理' }
])

// 新分类表单
const newCategory = reactive({
  name: '',
  description: '',
  severity_level: 'medium',
  is_active: true
})

// 编辑分类
const editingCategory = ref(null)
const showEditModal = ref(false)

// 筛选分类
const filteredCategories = computed(() => {
  if (!searchQuery.value) {
    return categories.value
  }
  return categories.value.filter(category => 
    category.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchQuery.value.toLowerCase()))
  )
})

// 监听显示状态
watch(() => props.show, (newValue) => {
  if (newValue) {
    activeTab.value = 'create'
    resetForm()
    loadCategories()
  }
})

// 重置表单
const resetForm = () => {
  newCategory.name = ''
  newCategory.description = ''
  newCategory.severity_level = 'medium'
  newCategory.is_active = true
  errors.value = {}
  searchQuery.value = ''
}

// 关闭模态框
const handleCancel = () => {
  activeTab.value = 'create'
  resetForm()
  emit('close')
}

// 加载分类
const loadCategories = async () => {
  try {
    loading.value = true
    const response = await sensitiveWordCategoryService.getCategories()
    // response已经是API的直接响应，包含results字段
    categories.value = response.results || response || []
  } catch (error) {
    console.error('Failed to load categories:', error)
    toast.error('加载分类失败')
  } finally {
    loading.value = false
  }
}

// 创建分类
const handleCreateCategory = async () => {
  if (!newCategory.name.trim()) {
    errors.value = { name: '分类名称不能为空' }
    return
  }

  try {
    submitting.value = true
    errors.value = {}
    
    const categoryData = {
      name: newCategory.name.trim(),
      description: newCategory.description.trim() || '',
      severity_level: newCategory.severity_level,
      is_active: newCategory.is_active
    }
    
    const result = await sensitiveWordCategoryService.createCategory(categoryData)
    if (result.success) {
      toast.success('分类创建成功')
    } else {
      throw new Error(result.message || '创建失败')
    }
    
    // 重置表单并切换到管理页面
    resetForm()
    activeTab.value = 'manage'
    await loadCategories()
    emit('success')
  } catch (error) {
    console.error('Failed to create category:', error)
    if (error.response?.data) {
      errors.value = error.response.data
    } else {
      toast.error('创建分类失败')
    }
  } finally {
    submitting.value = false
  }
}

// 编辑分类
const handleEditCategory = (category) => {
  editingCategory.value = { ...category }
  showEditModal.value = true
}

// 关闭编辑模态框
const handleCloseEditModal = () => {
  editingCategory.value = null
  showEditModal.value = false
  errors.value = {}
}

// 更新分类
const handleUpdateCategory = async () => {
  if (!editingCategory.value.name.trim()) {
    errors.value = { name: '分类名称不能为空' }
    return
  }

  try {
    submitting.value = true
    errors.value = {}
    
    const categoryData = {
      name: editingCategory.value.name.trim(),
      description: editingCategory.value.description?.trim() || '',
      severity_level: editingCategory.value.severity_level,
      is_active: editingCategory.value.is_active
    }
    
    const result = await sensitiveWordCategoryService.updateCategory(editingCategory.value.id, categoryData)
    if (result.success) {
      toast.success('分类更新成功')
    } else {
      throw new Error(result.message || '更新失败')
    }
    
    handleCloseEditModal()
    await loadCategories()
    emit('success')
  } catch (error) {
    console.error('Failed to update category:', error)
    if (error.response?.data) {
      errors.value = error.response.data
    } else {
      toast.error('更新分类失败')
    }
  } finally {
    submitting.value = false
  }
}

// 删除分类
const handleDeleteCategory = async (category) => {
  if (category.word_count > 0) {
    toast.error('该分类下还有敏感词，无法删除')
    return
  }

  if (!confirm(`确定要删除分类"${category.name}"吗？`)) {
    return
  }

  try {
    const result = await sensitiveWordCategoryService.deleteCategory(category.id)
    if (result.success) {
      toast.success('分类删除成功')
      await loadCategories()
      emit('success')
    } else {
      throw new Error(result.message || '删除失败')
    }
  } catch (error) {
    console.error('Failed to delete category:', error)
    toast.error(error.message || '删除分类失败')
  }
}

// 获取严重级别徽章样式
const getSeverityBadgeClass = (severity) => {
  const classes = {
    low: 'bg-blue-100 text-blue-800',
    medium: 'bg-yellow-100 text-yellow-800', 
    high: 'bg-orange-100 text-orange-800',
    forbidden: 'bg-red-100 text-red-800'
  }
  return classes[severity] || 'bg-gray-100 text-gray-800'
}

// 获取严重级别文本
const getSeverityText = (severity) => {
  const texts = {
    low: '低风险',
    medium: '中风险',
    high: '高风险', 
    forbidden: '禁止使用'
  }
  return texts[severity] || severity
}
</script>